/** 管理 Canvas 图片变形会话，依赖图片渲染、图片更新事务和舞台交互服务 */
import { cloneDeep, isEqual, pick } from 'lodash';
import { getFramework, type IFramework } from '@/framework';
import type { ImageLayer } from '@/layers/image-layer';
import { getImageLayerCanvas } from '@/layers/image-layer/render/render-to-stage';
import type { ContextMenuPlugin } from '@/plugins/context-menu/context-menu';
import type { MouseOperate } from '@/plugins/mouse-operate';
import { loadImageAsync } from '@/utils/file-kit';
import { rotatePoint } from '@/utils/math-kit';
import { uploadBase64ToObs } from '@/utils/upload-kit';
import { LayerType, type IImageLayerProps, type ILayerProps } from '@/version-types';
import { EDeformationImgType, getDeformationImgState } from './states/deformation-img';
import { EDeformationEvent, type IDeformationOffset } from './types';

/** 控制图片透视和扭曲预览，并将确认结果提交为一条图片历史 */
export class DeformationImgPlugin {
    private framework: IFramework;
    private originLayer: ILayerProps | null = null;
    private sessionGeneration = 0;
    private previousDisablePick = false;

    constructor(id: string) {
        this.framework = getFramework(id);
    }

    private get store() {
        return getDeformationImgState(this.framework.identity);
    }

    private getContextMenu() {
        return this.framework.service.registration.getInstance<ContextMenuPlugin>('ContextMenuPlugin');
    }

    private getMouseOperate() {
        return this.framework.service.registration.getInstance<MouseOperate>('MouseOperate');
    }

    private refreshFrame(layerId: string) {
        const { frame, edgelessCanvas } = this.framework.service;
        const frameId = frame.getLayerFrameId(layerId);
        if (typeof frameId !== 'number') return;
        edgelessCanvas.addDirtyFrameId(frameId);
        edgelessCanvas.requestRenderAll();
    }

    private getSourceInfo(layer: ILayerProps) {
        return {
            ...pick(layer, ['left', 'top', 'width', 'height', 'rotate']),
            ...pick(layer.data as IImageLayerProps, ['url', 'imageTransform', 'maskURL', 'cutoutMaskURL']),
        };
    }

    private finishSession(layerId?: string) {
        this.sessionGeneration += 1;
        if (this.originLayer) {
            this.getMouseOperate()?.setActiveRenderEnabled(true);
            this.getContextMenu()?.enableMenu();
            this.framework.service.editor.setDisablePick(this.previousDisablePick);
        }
        this.originLayer = null;
        this.store.reset();
        if (layerId && this.framework.service.flatLayer.getLayer(layerId)) {
            this.framework.service.frame.setActiveLayer(layerId);
        }
        this.framework.service.events.emit(EDeformationEvent.CLOSED);
    }

    /**
     * 读取新版 Canvas 图片本体并打开变形会话
     *
     * @param type - 透视或扭曲模式
     * @param layerId - 目标图片 ID
     * @returns 图片仍有效且成功打开时返回 true
     *
     * 副作用：隐藏原图片和普通选框，禁止切换选择。
     */
    async openForLayer(type: EDeformationImgType, layerId: string) {
        if (this.originLayer || this.store.isSaving) return false;
        const layer = this.framework.service.flatLayer.getLayer(layerId, true);
        if (!layer || layer.layerType !== LayerType.IMAGE_LAYER || layer.lock) return false;
        const generation = ++this.sessionGeneration;
        const layerImage = await getImageLayerCanvas(layer, this.framework.identity, {
            noEffect: true,
            globalAlpha: false,
            noNeedBorderRadius: true,
        });
        const latestLayer = this.framework.service.flatLayer.getLayer(layerId, true);
        if (generation !== this.sessionGeneration || !latestLayer || !isEqual(layer, latestLayer)) return false;
        return this.open(type, layerImage, layerId);
    }

    /**
     * 使用已渲染的图片本体打开变形会话
     *
     * @param type - 透视或扭曲模式
     * @param layerImage - 已应用裁剪及蒙版的图片本体
     * @param layerId - 可选；省略时使用当前选中图片
     * @returns 是否成功打开
     *
     * 副作用：标记原图片为变形状态并关闭普通选框。
     */
    open(type: EDeformationImgType, layerImage: HTMLCanvasElement, layerId?: string) {
        if (this.originLayer) this.close();
        const { frame, flatLayer, editor } = this.framework.service;
        const layer = layerId
            ? flatLayer.getLayer(layerId, true)
            : frame.getActivePickLayer(true) || frame.getActiveLayer();
        if (!layer || layer.layerType !== LayerType.IMAGE_LAYER || layer.lock) return false;
        this.originLayer = cloneDeep(layer);
        this.sessionGeneration += 1;
        this.previousDisablePick = editor.disablePick;
        this.store.reset();
        this.store.setCurrentType(type);
        this.store.setLayerImage(layerImage);
        this.store.setCurrentLayer(cloneDeep(layer));
        frame.addOrUpdateLayer({ ...layer, __isDeformationImage: true });
        this.getMouseOperate()?.setActiveRenderEnabled(false);
        this.getContextMenu()?.disableMenu();
        editor.setDisablePick(true);
        frame.setActiveLayer(layer.id);
        this.store.setIsDeformationImage(true);
        this.refreshFrame(layer.id);
        return true;
    }

    /**
     * 监听当前图片变形结果
     * @param callback - 接收结果图片与其局部偏移的回调
     * @returns 取消监听函数
     */
    onConfirm(callback: (image: HTMLCanvasElement, offset: IDeformationOffset) => void) {
        const handler = this.framework.service.events.on(EDeformationEvent.CONFIRM, callback);
        return () => this.framework.service.events.off(handler);
    }

    /**
     * 监听变形取消操作
     * @param callback - 取消操作回调
     * @returns 取消监听函数
     */
    onCancel(callback: () => void) {
        const handler = this.framework.service.events.on(EDeformationEvent.CANCEL, callback);
        return () => this.framework.service.events.off(handler);
    }

    /**
     * 监听会话结束以清理业务面板，无论会话由保存、取消还是初始化结束
     * @param callback - 会话结束回调
     * @returns 取消监听函数
     */
    onClose(callback: () => void) {
        const handler = this.framework.service.events.on(EDeformationEvent.CLOSED, callback);
        return () => this.framework.service.events.off(handler);
    }

    /**
     * 监听预览失败以提示用户
     * @param callback - 错误处理回调；第二个参数表示是否需要结束会话
     * @returns 取消监听函数
     */
    onError(callback: (error: unknown, shouldClose: boolean) => void) {
        const handler = this.framework.service.events.on(EDeformationEvent.ERROR, callback);
        return () => this.framework.service.events.off(handler);
    }

    /**
     * 读取当前会话绑定的图片快照
     * @returns 当前图片的独立副本；没有变形会话时返回 null
     */
    getCurrentLayer() {
        return this.originLayer ? cloneDeep(this.originLayer) : null;
    }

    /**
     * 上传变形结果并通过新版图片事务提交
     *
     * @param image - 变形后的图片本体
     * @param formatLayer - 可选；自定义提交函数，须负责写入图片及历史
     * @param offset - 变形结果相对原图片左上角的局部偏移
     * @returns 成功提交返回 true；会话失效或目标删除时返回 false
     *
     * 副作用：上传 PNG 并写入一条历史；上传失败保留当前会话供重试。
     */
    async save(
        image: HTMLCanvasElement,
        formatLayer?: (layer: ILayerProps, originLayer: ILayerProps) => Promise<void>,
        offset: IDeformationOffset = { left: 0, top: 0 },
    ) {
        const originLayer = this.originLayer;
        if (!originLayer || this.store.isSaving || image.width <= 0 || image.height <= 0) return false;
        const generation = this.sessionGeneration;
        const isCurrent = () => generation === this.sessionGeneration && this.originLayer?.id === originLayer.id;
        this.store.setIsSaving(true);
        try {
            const result = await uploadBase64ToObs(image.toDataURL('image/png'));
            if (!result?.url) throw new Error('Upload deformation image failed');
            if (!isCurrent()) return false;
            await loadImageAsync(result.url);
            if (!isCurrent()) return false;
            const latestLayer = this.framework.service.flatLayer.getLayer(originLayer.id, true);
            if (!latestLayer) {
                this.finishSession();
                return false;
            }
            // 异步上传期间源图或几何被其他操作修改时，保留最新结果并取消过期的变形会话。
            if (!isEqual(this.getSourceInfo(originLayer), this.getSourceInfo(latestLayer))) {
                this.close();
                return false;
            }
            const oldLayer = { ...cloneDeep(latestLayer), __isDeformationImage: false };
            const newLayer = cloneDeep(oldLayer);
            const data = newLayer.data as IImageLayerProps;
            const centerOffset = rotatePoint(
                {
                    x: offset.left + (image.width - originLayer.width) / 2,
                    y: offset.top + (image.height - originLayer.height) / 2,
                },
                originLayer.rotate,
            );
            newLayer.left = originLayer.left + originLayer.width / 2 + centerOffset.x - image.width / 2;
            newLayer.top = originLayer.top + originLayer.height / 2 + centerOffset.y - image.height / 2;
            newLayer.width = image.width;
            newLayer.height = image.height;
            data.url = result.url;
            data.naturalWidth = image.width;
            data.naturalHeight = image.height;
            data.maskURL = '';
            data.cutoutMaskURL = '';
            data.imageTransform = {
                scale: 1,
                rotate: 0,
                offsetX: 0,
                offsetY: 0,
                flipH: false,
                flipV: false,
                widthRatio: 1,
                heightRatio: 1,
            };
            delete newLayer.extraInfo?.mattingInfo;
            delete newLayer.extraInfo?.mattingOriginalURL;
            if (formatLayer) {
                await formatLayer(newLayer, oldLayer);
            } else {
                const imageLayer = this.framework.service.registration.getInstance<ImageLayer>('ImageLayer');
                await imageLayer.updateLayer(newLayer, oldLayer, true, undefined, { canCommit: isCurrent });
            }
            if (!isCurrent()) return false;
            this.finishSession(newLayer.id);
            this.refreshFrame(newLayer.id);
            return true;
        } finally {
            if (isCurrent()) this.store.setIsSaving(false);
        }
    }

    /** 清除变形运行时标记，保留目标图片的最新业务数据；不写历史 */
    reset() {
        if (!this.originLayer) return;
        const layer = this.framework.service.flatLayer.getLayer(this.originLayer.id, true);
        if (!layer) return;
        this.framework.service.frame.addOrUpdateLayer({ ...layer, __isDeformationImage: false });
        this.refreshFrame(layer.id);
    }

    /** 取消当前会话并恢复舞台交互；取消也会使尚未完成的上传失效 */
    close() {
        const layerId = this.originLayer?.id;
        this.reset();
        this.finishSession(layerId);
    }

    /** 销毁插件并取消尚未提交的变形会话 */
    destroy() {
        this.close();
    }
}
